import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import axios from 'axios'
import { FeatureFlags } from '@/options/featureFlags.js'

Vue.use(VueRouter)

// Lazy load components for better performance
const routes = [

  // Root route - redirect based on auth state
  {
    path: '/',
    beforeEnter: (_to, _from, next) => {
      if (window.IS_MOBILE_APP && !localStorage.getItem('jwt_token')) {
        next('/login')
      } else {
        next('/agents')
      }
    }
  },

  // Server redirects / to /admins — handle it the same way
  {
    path: '/admins',
    beforeEnter: (_to, _from, next) => {
      if (window.IS_MOBILE_APP && !localStorage.getItem('jwt_token')) {
        next('/login')
      } else {
        next('/agents')
      }
    }
  },

  // Unified login (JWT for mobile app, session for web)
  {
    path: '/login',
    name: 'login',
    component: () => import('@/vue-pages/Login.vue'),
    meta: { requiresAuth: false, title: 'Sign In' }
  },

  // Magic link landing — reads ?key=... from URL, exchanges for session/JWT, redirects.
  {
    path: '/login/magic-link',
    name: 'magic-link',
    component: () => import('@/vue-pages/magicLink.vue'),
    meta: { requiresAuth: false, title: 'Signing you in...' }
  },

  // Web app hits /aLogin (PHP route) — redirect to Vue login
  {
    path: '/aLogin',
    redirect: '/login'
  },

  // Dashboard - root route
  {
    path: '/agents',
    name: 'dashboard', 
    component: () => import('@/vue-pages/dashboardNew.vue'),
    meta: { requiresAuth: true, title: 'Dashboard' }
  },

    {
        path: '/agents/onboardingFlow',
        name: 'onboardingFlow',
        component: () => import('@/vue-pages/OnboardingFlow/index.vue'),
        meta: { requiresAuth: true, title: 'Onboarding'}
    },
    {
        path: '/agents/cancellationFlow',
        name: 'cancellationFlow',
        component: () => import('@/vue-pages/HouseJet/CancellationFlowPreview.vue'),
        meta: { requiresAuth: true, title: 'Cancellation Flow'}
    },
    {
        path: '/agents/onboardingFlow2',
        name: 'onboardingFlow2',
        component: () => import('@/vue-pages/OnboardingFlow/index2.vue'),
        meta: { requiresAuth: true, title: 'Onboarding'}
    },
    {
        path: '/agents/onboardingFlow3',
        name: 'onboardingFlow3',
        component: () => import('@/vue-pages/OnboardingFlow/index3.vue'),
        meta: { requiresAuth: true, title: 'Onboarding'}
    },

  // Core Agent Routes
  {
    path: '/agents/pipeline/grid',
    name: 'pipeline',
    component: () => import('@/vue-pages/pipeline.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_PIPELINES, title: 'Pipeline' }
  },
  {
    path: '/agents/userList',
    name: 'user-list',
    component: () => import('@/vue-pages/leadlist/index.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_CONTACTS, title: 'Contacts' },
  },
  {
    path: '/agents/userProfile',
    name: 'profile',
    component: () => import('@/vue-pages/profile.vue'),
    meta: { requiresAuth: true, title: 'Profile' },
    props: route => ({ id: route.query.id })
  },
  {
    path: '/agents/tasks',
    name: 'tasks',
    component: () => import('@/vue-pages/tasks.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_TASKS, title: 'Tasks' }
  },
  {
    path: '/agents/calendar',
    name: 'calendar',
    component: () => import('@/vue-pages/calendar.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_CALENDAR, title: 'Calendar' }
  },
  {
    path: '/agents/events',
    name: 'events',
    component: () => import('@/vue-pages/events/events.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_EVENTS, title: 'Events' }
  },
  {
    path: '/agents/events/add',
    name: 'add-event',
    component: () => import('@/vue-pages/events/create_event.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_EVENTS, title: 'New Event' },
    props: route => ({ id: route.query.id })
  },
  {
    path: '/agents/events/edit',
    name: 'edit-event',
    component: () => import('@/vue-pages/events/edit_event.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_EVENTS, title: 'Edit Event' },
    props: route => ({ id: route.query.id })
  },
  {
    path: '/agents/events/report',
    name: 'event-report',
    component: () => import('@/vue-pages/events/event_report.vue'),
    meta: { requiresAuth: true },
    props: route => ({ id: route.query.id })
  },
  {
    path: '/agents/notifications',
    name: 'notifications',
    component: () => import('@/vue-pages/notifications.vue'),
    meta: { requiresAuth: true, title: 'Notifications' }
  },
  {
    path: '/agents/connectedAccounts',
    name: 'connected-accounts',
    component: () => import('@/vue-pages/accountConnections.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_CONNECTED_ACCOUNTS, title: 'Connected Accounts' }
  },
  {
    path: '/agents/viewReminderSequences',
    name: 'view-reminder-sequences',
    component: () => import('@/vue-pages/reminderSequence/index.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_HJ_PLUS,  title: 'Reminder Sequences' }
  },
  {
    path: '/agents/editReminderSequences',
    name: 'edit-reminder-sequences',
    component: () => import('@/vue-pages/reminderSequence/Edit.vue'),
    meta: { requiresAuth: true, title: 'Edit Reminder Sequences' },
    props: route => ({ id: route.query.id })
  },
  {
    path: '/agents/importWizard',
    name: 'import-wizard',
    component: () => import('@/vue-pages/importWizard.vue'),
    meta: { requiresAuth: true, title: 'Import Wizard' },
    beforeEnter: async (_to, _from, next) => {
      try {
        // Check for pending/running imports
        const response = await axios.get('/agents/ajax/checkPendingImport')
        if (response.data.hasPendingImport && response.data.importId) {
          // Redirect to status page if there's a pending import
          next({ name: 'import-wizard-status', query: { id: response.data.importId } })
        } else {
          next()
        }
      } catch (error) {
        console.error('Error checking pending imports:', error)
        next() // Continue to import wizard on error
      }
    }
  },
  {
    path: '/agents/importWizardStatus',
    name: 'import-wizard-status',
    component: () => import('@/vue-pages/importStatus.vue'),
    meta: { requiresAuth: true, title: 'Import Wizard Status' },
    props: route => ({ id: route.query.id })
  },
  {
    path: '/agents/preLogout',
    name: 'prelogout',
    component: () => import('@/vue-pages/logout.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/agents/sellerReverseSearch',
    name: 'seller-reverse-search',
    component: () => import('@/vue-pages/marketing/mls/seller-reverse-search.vue'),
    meta: { requiresAuth: true, title: 'Seller Reverse Search' }
  },

  // AI Dialer Campaign
  {
    path: '/agents/ai-dialer/campaign',
    name: 'ai-dialer-campaign',
    component: () => import('@/components/AiDialer/CallCampaign.vue'),
    meta: { requiresAuth: true, title: 'AI Dialer Campaign' },
    props: route => ({ id: route.query.id })
  },
  {
    path: '/agents/ai-dialer/activity-log',
    name: 'ai-dialer-activity-log',
    component: () => import('@/components/AiDialer/AiDialerActivityLog.vue'),
    meta: { requiresAuth: true, title: 'AI Dialer Activity Log' },
  },
  {
    path: '/agents/ai-dialer/activity-log/:id',
    name: 'ai-dialer-activity-log-leads',
    component: () => import('@/components/AiDialer/ActivityLogLeads.vue'),
    meta: { requiresAuth: true, title: 'AI Dialer Campaign Leads' },
    props: true
  },

  // Marketing Routes
  {
    path: '/agents/marketing/blast',
    name: 'marketing-blast',
    component: () => import('@/vue-pages/productivity/email/email-campaigns/index.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_EMAIL_BLASTS,  title: 'Email Blasts' }
  },
  {
    path: '/agents/marketing/editCampaign',
    name: 'marketing-edit-campaign',
    component: () => import('@/vue-pages/productivity/email/email-campaigns/upsert.vue'),
    meta: { requiresAuth: true, title: 'Edit Campaign' },
    props: route => ({ id: route.query.id })
  },
  {
    path: '/agents/marketing/campaignStats',
    name: 'marketing-campaign-stats',
    component: () => import('@/vue-pages/productivity/email/email-campaigns/stats.vue'),
    meta: { requiresAuth: true, title: 'Email Blasts' },
    props: route => ({ id: route.query.id })
  },
  {
    path: '/agents/marketing/addCustomPage',
    name: 'marketing-add-custom-page',
    component: () => import('@/vue-pages/marketing/website/page-builder/add.vue'),
    meta: { requiresAuth: true, title: 'Add Custom Page' }
  },
  {
    path: '/agents/emailTemplates',
    name: 'email-templates',
    component: () => import('@/vue-pages/productivity/emailTemplates.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_EMAIL_BLASTS,  title: 'Email Templates' }
  },
  {
    path: '/agents/mailbox',
    name: 'mailbox',
    component: () => import('@/vue-pages/productivity/mailbox.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_MAILBOX, title: 'Mailbox' }
  },
  {
    path: '/agents/marketing/mlsSearch',
    name: 'mls-search',
    component: () => import('@/vue-pages/productivity/mlsSearch/index.vue'),
    meta: { requiresAuth: true, title: 'MLS Search' }
  },
  {
    path: '/agents/marketing/blogs',
    name: 'marketing-blogs',
    component: () => import('@/vue-pages/marketing/blogs/index.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_BLOGS,  title: 'Blogs' }
  },
  {
    path: '/agents/marketing/blogAddEdit',
    name: 'marketing-blog-add-edit',
    component: () => import('@/vue-pages/marketing/blogAddEdit.vue'),
    meta: { requiresAuth: true, title: 'Blog' }
  },
  {
    path: '/agents/marketing/videoAddEdit',
    name: 'marketing-video-add-edit',
    component: () => import('@/vue-pages/marketing/blogAddEdit.vue'),
    meta: { requiresAuth: true, title: 'Video' }
  },
  {
    path: '/agents/marketing/blogAddEditAI',
    name: 'marketing-blog-add-edit-ai',
    component: () => import('@/vue-pages/marketing/blogAddEditAI.vue'),
    meta: { requiresAuth: true, title: 'AI Blog' }
  },
  {
    path: '/agents/marketing/videos',
    name: 'marketing-videos',
    component: () => import('@/vue-pages/marketing/videos.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_VIDEOS,  title: 'Videos' }
  },
  {
    path: '/agents/marketing/branding',
    name: 'marketing-branding',
    component: () => import('@/vue-pages/HouseJet/Branding.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_SOCIAL_BRANDING,  title: 'Social Branding' }
  },
  {
    path: '/agents/marketing/campaigns',
    name: 'marketing-campaigns',
    component: () => import('@/vue-pages/marketing/Campaigns.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: [FeatureFlags.KEY_ADDITIONAL_MARKETING_LEAD_GEN, FeatureFlags.KEY_ADDITIONAL_MARKETING_RETARGETING],  title: 'Additional Marketing' }
  },
  {
    path: '/agents/marketing/campaignCreator',
    name: 'marketing-campaign-creator',
    component: () => import('@/vue-pages/marketing/CampaignCreator.vue'),
    meta: { requiresAuth: true, title: 'New Campaign' }
  },
  {
    path: '/agents/marketing/smartLists',
    name: 'marketing-smart-lists',
    component: () => import('@/vue-pages/marketing/SmartLists.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_ADDITIONAL_MARKETING_SMART_LISTS,  title: 'Smart Lists' }
  },
  {
    path: '/agents/marketing/surveyList',
    name: 'marketing-surveys',
    component: () => import('@/vue-pages/productivity/surveys/index.vue'),
    meta: { requiresAuth: true, title: 'Surveys' }
  },
  {
    path: '/agents/marketing/surveyStatistics',
    name: 'marketing-survey-statistics',
    component: () => import('@/vue-pages/productivity/surveys/statistics.vue'),
    meta: { requiresAuth: true, title: 'View Survey' }
  },
  {
    path: '/agents/marketing/surveyAddEdit',
    name: 'marketing-survey-add-edit',
    component: () => import('@/vue-pages/productivity/surveys/upsert.vue'),
    meta: { requiresAuth: true, title: 'Survey' }
  },
  {
    path: '/agents/marketing/customPages',
    name: 'marketing-custom-pages',
    component: () => import('@/vue-pages/marketing/website/page-builder/index.vue'),
    meta: { requiresAuth: true, title: 'Custom Pages (Beta)' }
  },
  {
    path: '/agents/marketing/customPages/view',    
    name: 'marketing-custom-page-view',
    component: () => import('@/vue-pages/marketing/website/page-builder/view.vue'),
    meta: { requiresAuth: true, title: 'View Custom Page (Beta)' }
  },
  {
    path: '/agents/marketing/domains',
    name: 'marketing-domains',
    component: () => import('@/vue-pages/marketing/website/domain.vue'),
    meta: { requiresAuth: true, title: 'Domains' }
  },
  {
    path: '/agents/leadFlowAddresses',
    name: 'lead-flow-addresses',
    component: () => import('@/vue-pages/leadFlowAddresses.vue'),
    meta: { requiresAuth: true, title: 'Lead Flow Addresses' }
  },
  {
    path: '/styleGuide',
    name: 'styleGuide',
    component: () => import('@/vue-pages/styleGuide.vue'),
    meta: { requiresAuth: true, title: 'Style Guide' },
    beforeEnter: (_to, _from, next) => {
      // Only allow access in non-production environments
      if (process.env.NODE_ENV === 'production') {
        next('/404')
      } else {
        next()
      }
    }
  },
  {
    path: '/agents/timelineCards',
    name: 'timelineCards',
    component: () => import('@/vue-pages/TimelineCardsPreview.vue'),
    meta: { requiresAuth: true, title: 'Timeline Cards Preview' },
    beforeEnter: (_to, _from, next) => {
      // Only allow access in non-production environments
      if (process.env.NODE_ENV === 'production') {
        next('/404')
      } else {
        next()
      }
    }
  },


  // Prospect List
  {
    path: '/agents/prospects',
    name: 'prospects',
    component: () => import('@/vue-pages/Prospects/prospects.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_PROSPECT_LIST, title: 'Prospect List' }
  },

  // Teams and Profile
  {
    path: '/agents/teams',
    name: 'teams',
    component: () => import('@/vue-pages/Teams/teams.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_TEAMS,  title: 'Teams' }
  },
  {
    // Deep link used by the team "new leads" notification, e.g. /agents/teams/12/leads
    path: '/agents/teams/:teamId/:tab(members|leads|invites|settings)?',
    name: 'team-tab',
    component: () => import('@/vue-pages/Teams/teams.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_TEAMS,  title: 'Teams' }
  },
  {
    path: '/agents/updateProfile',
    name: 'user-profile',
    component: () => import('@/vue-pages/updateProfile/index.vue'),
    meta: { requiresAuth: true, title: 'Profile' }
  },
    {
        path: '/agents/updateProfile#notifications',
        name: 'update-profile-notifications',
        component: () => import('@/vue-pages/updateProfile/index.vue'),
        meta: { requiresAuth: true, title: 'Profile' }
    },
  {
    path: '/agents/updateProfileNxlead',
    name: 'user-profile-nxlead',
    component: () => import('@/vue-pages/updateProfileNxLead/index.vue'),
    meta: { requiresAuth: true, title: 'Profile' }
  },
  {
    path: '/agents/agentFiles',
    name: 'agent-files',
    component: () => import('@/vue-pages/agentFiles.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_MY_FILES, title: 'Files' }
  },
  {
    path: '/house-jet/website/testimonials',
    name: 'housejet-website-testimonials',
    component: () => import('@/vue-pages/PPC/WebsiteTestimonials.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_WEBSITES, title: 'Website Testimonials' }
  },
  {
    path: '/agents/commissionShare',
    name: 'commission-share',
    component: () => import('@/vue-pages/CommissionShare.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_COMMISSION_SHARE_PROGRAM, title: 'Commission Share Program' }
  },
  {
    path: '/agents/serviceAreas',
    name: 'service-areas',
    component: () => import('@/vue-pages/ServiceAreas.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_SERVICE_AREAS, title: 'Service Areas' }
  },
  {
    path: '/agents/usage',
    name: 'usage',
    component: () => import('@/vue-pages/Usage.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_USAGE, title: 'Usage' }
  },
  {
    path: '/agents/upgrade',
    name: 'upgrade',
    component: () => import(/* webpackChunkName: "agents-upgrade" */ '@/vue-pages/upgrade.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_UPGRADE, title: 'Upgrade' }
  },
  {
    path: '/agents/calendlySettings',
    name: 'calendly-settings',
    component: () => import('@/vue-pages/calendlySettings.vue'),
    meta: { requiresAuth: true }
  },

  // Learning Center
  {
    path: '/agents/learningCenter/index',
    name: 'learning-center',
    component: () => import('@/vue-pages/learningCenter/index.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_UNIVERSITY, title: 'University' }
  },
  {
    path: '/agents/learning-center/tutorial',
    name: 'learning-center-tutorial',
    component: () => import('@/vue-pages/learningCenter/tutorial.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_UNIVERSITY, title: 'University' },
    props: route => ({ id: route.query.id })
  },

  // Support
  {
    path: '/agents/support',
    name: 'support',
    component: () => import('@/vue-pages/support.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_SUPPORT, title: 'Support' }
  },

    // Support
    {
        path: '/agents/support/index',
        component: () => import('@/vue-pages/support.vue'),
        meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_SUPPORT, title: 'Support' }
    },

  // Dialer Routes
  {
    path: '/agents/dialerCampaigns',
    name: 'dialer-campaigns',
    component: () => import('@/vue-pages/dialer/DialerCampaigns.vue'),
    meta: { requiresAuth: true, title: 'Dialer Campaigns' }
  },
  {
    path: '/agents/manageVoiceBlasts',
    name: 'dialer-voice-recordings',
    component: () => import('@/vue-pages/productivity/dialer/voiceRecordings.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_VOICE_RECORDINGS,  title: 'Voice Recordings' }
  },

  // Automation Routes
  {
    path: '/agents/viewPlansTemplates',
    redirect: '/agents/automations'
  },
  {
    path: '/agents/automations',
    name: 'automation-view',
    component: () => import('@/vue-pages/automation/Automations.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_AUTOMATIC_PLANS,  title: 'Automations' }
  },
  {
    path: '/agents/editPlanTemplate',
    redirect: to => ({ path: '/agents/editAutomation', query: to.query })
  },
  {
    path: '/agents/editAutomation',
    name: 'automation-edit',
    component: () => import('@/vue-pages/automation/EditAutomation.vue'),
    meta: { requiresAuth: true, title: 'Edit Automation', requiresFeatureFlag: FeatureFlags.KEY_AUTOMATIC_PLANS },
    props: route => ({ id: route.query.id })
  },
  {
    path: '/agents/viewAutomatedPlansReport',
    name: 'view-automated-plans-report',
    component: () => import('@/vue-pages/automation/AutomatedPlanStatsReport.vue'),
    meta: { requiresAuth: true, title: 'View Automation', requiresFeatureFlag: FeatureFlags.KEY_AUTOMATIC_PLANS }
  },

  // Administration Routes
  {
    path: '/administration/distributionSettings',
    name: 'administration-distribution-settings',
    component: () => import('@/vue-pages/administration/distributionSettings.vue'),
    meta: { requiresAuth: true, title: 'Distribution Settings' }
  },
  {
    path: '/administration/distributionSettingsAdd',
    name: 'admin-agent-settings',
    component: () => import('@/vue-pages/administration/distributionSettingsAdd.vue'),
    meta: { requiresAuth: true, title: 'Add Distribution Group' }
  },
  {
    path: '/administration/distributionSettingsEdit',
    name: 'admin-agent-settings-edit',
    component: () => import('@/vue-pages/administration/distributionSettingsEdit.vue'),
    meta: { requiresAuth: true, title: 'Edit Distribution Group' }
  },

  {
    path: '/administration/userTags',
    name: 'admin-user-tags',
    component: () => import('@/vue-pages/administration/userTags.vue'),
    meta: { requiresAuth: true, title: 'Tags' }
  },
  {
    path: '/administration/leadTypes',
    name: 'admin-lead-types',
    component: () => import('@/vue-pages/administration/leadTypes.vue'),
    meta: { requiresAuth: true, title: 'Lead Types' }
  },
  {
    path: '/administration/leadPools',
    name: 'admin-lead-pool',
    component: () => import('@/vue-pages/administration/leadPool.vue'),
    meta: { requiresAuth: true, title: 'Lead Pools' }
  },
  {
    path: '/administration/settingsAdmin',
    name: 'settings-admin',
    component: () => import('@/vue-pages/administration/settingsAdmin.vue'),
    meta: { requiresAuth: true, title: 'Settings' }
  },
  {
    path: '/administration/viewAdminUser',
    name: 'admin-users',
    component: () => import('@/vue-pages/administration/viewAdminUser.vue'),
    meta: { requiresAuth: true, title: 'User List' }
  },
  {
    path: '/administration/websiteSettings',
    name: 'website-settings',
    component: () => import('@/vue-pages/administration/websiteSettings.vue'),
    meta: { requiresAuth: true, title: 'Website Settings' }
  },
  {
    path: '/administration/tasksDispositions',
    name: 'tasks-dispositions',
    component: () => import('@/vue-pages/task_dispositions.vue'),
    meta: { requiresAuth: true, title: 'Disposition Settings' }
  },

  // Reporting Routes
  {
    path: '/agents/leadSourceReport',
    name: 'report-lead-source',
    component: () => import('@/vue-pages/reporting/reports/leadSourceReport.vue'),
    meta: { requiresAuth: true, title: 'Lead Source Report' }
  },
  {
    path: '/agents/taskCallReport',
    name: 'report-task-calls',
    component: () => import('@/vue-pages/reporting/reports/TaskCallsReport.vue'),
    meta: { requiresAuth: true, title: 'Task Call Report' }
  },
  {
    path: '/agents/taskDispositionReport',
    name: 'report-task-disposition',
    component: () => import('@/vue-pages/reporting/reports/taskDispositionReport.vue'),
    meta: { requiresAuth: true, title: 'Task Disposition Report' }
  },
  {
    path: '/agents/utmReport',
    name: 'report-utm',
    component: () => import('@/vue-pages/reporting/reports/utmReport.vue'),
    meta: { requiresAuth: true, title: 'UTM Parameters Report' }
  },
  {
    path: '/agents/salesHistory',
    name: 'sales-history',
    component: () => import('@/vue-pages/productivity/salesHistory.vue'),
    meta: { requiresAuth: true, title: 'Sales History' }
  },
  {
    path: '/administration/originReport',
    name: 'origin-report',
    component: () => import('@/vue-pages/reporting/reports/originReport.vue'),
    meta: { requiresAuth: true, title: 'Origin Report' }
  },

  // HouseJet Routes (for /house-jet/* URLs)
  {
    path: '/house-jet/plus',
    name: 'housejet-plus',
    component: () => import('@/vue-pages/HouseJet/Plus.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_HJ_PLUS,  title: 'Plus' }
  },
  {
    path: '/lead-config/marketing/setup',
    name: 'housejet-marketing-setup',
    component: () => import('@/vue-pages/HouseJet/ReferralMarketingSetup.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_REFERRAL_MARKETING,  title: 'Marketing Setup' },
    beforeEnter: async (_to, _from, next) => {
      try {
        // Check for marketing setup
        const response = await axios.get('/house-jet/marketing/checkCourseCompleteAndMembership')
        if (response.data.redirect) {
          // Redirect to /agents or university video depending on team membership and course
          next({ path: response.data.redirect })
        } else {
          next()
        }
      } catch (error) {
        console.error('Error checking marketing')
        next()
      }
    },
  },
  {
    path: '/lead-config/marketing',
    name: 'housejet-marketing',
    component: () => import('@/vue-pages/HouseJet/Marketing.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_REFERRAL_MARKETING,  title: 'Marketing' },
    beforeEnter: async (_to, _from, next) => {
      try {
        // Check for marketing setup
        const response = await axios.get('/house-jet/marketing/checkMarketingSetup')
        if (response.data.redirect) {
          // Redirect to marketing wizard page if its not setup
          next({ path: response.data.redirect })
        } else {
          next()
        }
      } catch (error) {
        console.error('Error checking marketing setup')
        next()
      }
  },
    
  },
  {
    path: '/house-jet/plus/feature',
    name: 'housejet-feature',
    component: () => import('@/vue-pages/HouseJet/Feature.vue'),
    meta: { requiresAuth: true, title: 'Feature' }
  },
  {
    path: '/house-jet/upload-photos',
    name: 'housejet-upload-photos',
    component: () => import('@/vue-pages/HouseJet/UploadPhotos.vue'),
    meta: { requiresAuth: true, title: 'Upload Photos' }
  },
  {
    path: '/house-jet/website/setup',
    name: 'housejet-website-setup',
    component: () => import('@/vue-pages/PPC/Website.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_WEBSITES, title: 'Website' }
  },
  {
    path: '/house-jet/website/edit',
    name: 'housejet-website-edit',
    component: () => import('@/vue-pages/PPC/WebsiteEdit.vue'),
    meta: { requiresAuth: true, title: 'Edit Website' }
  },
  {
    path: '/house-jet/website/disclosures',
    name: 'housejet-website-disclosures',
    component: () => import('@/vue-pages/PPC/Disclosures.vue'),
    meta: { requiresAuth: true, title: 'Disclosures' }
  },
  {
    path: '/ppc/billing',
    name: 'ppc-billing',
    component: () => import('@/vue-pages/PPC/Billing.vue'),
    meta: { requiresAuth: true, requiresFeatureFlag: FeatureFlags.KEY_BILLING, title: 'Billing' }
  },
  {
    path: '/agents/billingProfile',
    name: 'housejet-billing-profile',
    component: () => import('@/vue-pages/billingProfile.vue'),
    meta: { requiresAuth: true, title: 'Billing Profile' },
    props: { userRole: 'agent', currentPage: '/agents/billingProfile' }
  },
  {
    path: '/agents/addBillingProfile',
    name: 'housejet-add-billing-profile',
    component: () => import('@/vue-pages/agents/addBillingProfile.vue'),
    meta: { requiresAuth: true },
  },
  // PPC Routes (for /ppc/* URLs)
  {
    path: '/ppc/register/precheck',
    name: 'ppc-register-precheck',
    component: () => import('@/vue-pages/PPC/Precheck.vue'),
    props: route => ({ key: route.query.key })
  },
  {
    path: '/ppc/register/new',
    name: 'ppc-register-new',
    component: () => import('@/vue-pages/PPC/New.vue'),
    props: route => ({ key: route.query.key })
  },
  {
    path: '/ppc/join-team',
    name: 'ppc-join-team',
    component: () => import('@/vue-pages/PPC/JoinTeam.vue'),
    meta: { requiresAuth: false, title: 'Join Team' },
    props: route => ({ token: route.query.token })
  },
  // Standalone product sign-up page — /sign-up/{product-key}
  {
    path: '/sign-up/:productKey',
    name: 'ppc-sign-up',
    component: () => import('@/vue-pages/PPC/SignUp.vue'),
    meta: { requiresAuth: false, title: 'Sign Up' },
    props: route => ({ productKey: route.params.productKey })
  },

  // Lender Portal we may use this in the future
  {
    path: '/lender/lead/view',
    name: 'lender-lead-view',
    meta: {title: 'Lender Lead View'},
    component: () => import('@/vue-pages/Lender/View.vue'),
  },
  // 404 page for unmatched routes - must be last
  {
    path: '/404',
    name: '404-page',
    meta: { title: '404' },
    component: () => import('@/vue-pages/404page.vue')
  },
  // Catch all route - must be last

  {
    path: '/teams/*',
    redirect: '/teams'
  },
  // Catch all - 404s
  {
    path: '/agents/*',
      component: () => import('@/vue-pages/404page.vue')
  },
  {
    path: '/admin/*',
      component: () => import('@/vue-pages/404page.vue')
  },
  {
    path: '/house-jet/*',
      component: () => import('@/vue-pages/404page.vue')
  },
  {
    path: '/house-jet/marketing/*',
      component: () => import('@/vue-pages/404page.vue')
  },
  {
    path: '/ppc/*',
      component: () => import('@/vue-pages/404page.vue')
  },
  {
    path: '*',
      component: () => import('@/vue-pages/404page.vue')
  }

]

const router = new VueRouter({
  mode: 'history',
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  }
})

router.beforeResolve((to, from, next) => {
    next()
})

// Navigation guards
router.beforeEach(async (to, _from, next) => {
    await store.commit('navigation/setNavigating', true)
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const requiresFeatureFlag = to.matched.find(record => record.meta.requiresFeatureFlag)?.meta.requiresFeatureFlag
  const title = to.meta.title;
  document.title = title;

  if (requiresAuth) {
    const isMobile = window.IS_MOBILE_APP
    const authRedirect = '/login'

    let currentUser = store.getters['current_user/getCurrentUser']
    const modified = store.state.current_user.modified;
    document.title = (currentUser?.brandName && title !== currentUser.brandName) ? `${title} | ${currentUser.brandName}` : `${title}`;

    // Check if we have a valid user that's less than 5 minutes old (same as getCurrentUserX mixin logic)
    if (currentUser?.id && (Date.now() - modified) < 300000) {
      // Check if user can access route
      if (!redirectToBillingIfPaymentFailed(currentUser, to, next)) return
      if (!currentUserCanAccess(currentUser, requiresFeatureFlag, next)) {
        return
      }
      next()
      return
    }

    // Check if we already have CURRENT_USER from window object (set by backend)
    // Skip on mobile app — window.CURRENT_USER is not set (no server-rendered page)
    if (!isMobile && window.CURRENT_USER?.id) {
      console.log('Setting user from window.CURRENT_USER')
      store.commit('current_user/setCurrentUser', window.CURRENT_USER)
      currentUser = window.CURRENT_USER

      // Check if user can access route
      if (!redirectToBillingIfPaymentFailed(currentUser, to, next)) return
      if (!currentUserCanAccess(currentUser, requiresFeatureFlag, next)) {
        return
      }
      next()
      return
    }

    // On mobile, check for JWT token before attempting API call
    if (isMobile && !localStorage.getItem('jwt_token')) {
      console.log('Mobile app: no JWT token, redirecting to login')
      next(authRedirect)
      return
    }

    console.log('No valid user in store, attempting to fetch')
    try {
      await store.dispatch('current_user/fetchCurrentUser')
      currentUser = store.getters['current_user/getCurrentUser']
    } catch (error) {
      console.log('Failed to fetch current user:', error)
      // Prevent redirect loop by checking if we're already trying to go to the redirect target
      if (to.path !== authRedirect) {
        next(authRedirect)
      } else {
        next(false) // Cancel navigation to prevent loop
      }
      return
    }

    // Final check - ensure we have a valid user
    if (!currentUser || !currentUser.id) {
      console.log('No valid user found, redirecting')
      // Prevent redirect loop by checking if we're already trying to go to the redirect target
      if (to.path !== authRedirect) {
        next(authRedirect)
      } else {
        next(false) // Cancel navigation to prevent loop
      }
      return
    }

    // Check if user can access route
    if (!redirectToBillingIfPaymentFailed(currentUser, to, next)) return
    if (!currentUserCanAccess(currentUser, requiresFeatureFlag, next)) {
      return
    }
  }

  next()
})

const BILLING_ALLOWED_PATHS = ['/ppc/billing', '/agents/billingProfile', '/agents/addBillingProfile', '/agents/support/index']
function redirectToBillingIfPaymentFailed(currentUser, to, next) {
  if (!currentUser?.is_payment_failed) return true
  const path = (to.path || '').replace(/\/$/, '')
  if (BILLING_ALLOWED_PATHS.includes(path)) return true
  next({ name: 'ppc-billing' })
  return false
}

function currentUserCanAccess(currentUser, requiresFeatureFlag, next){
  // Check feature flag if required
  if (requiresFeatureFlag) {
    const platform = currentUser?.client?.platform || 'ppc'
    if (platform !== 'nxlead') {
      // Prevent things from flashing during hydration
      if (!currentUser?.enabled_features) {
        console.log('User enabled_features not loaded')
        next('/agents')
        return false
      }

      let hasAccess = false
      if (Array.isArray(requiresFeatureFlag)) {
        hasAccess = requiresFeatureFlag.some(flag => {
          return currentUser.enabled_features.indexOf(flag) !== -1
        })
      } else {
        hasAccess = currentUser.enabled_features.indexOf(requiresFeatureFlag) !== -1
      }

      if (!hasAccess) {
        console.log('User does not have required feature flag:', requiresFeatureFlag)
        next('/agents')
        return false
      }
    }
  }

  return true
}

router.afterEach(() => {
    store.commit('navigation/setNavigating', false)
    if ( window.userpilot ){
        // Uses the userpilot object created by Userpilot.initialize, we don't import UserPilot here
        window.userpilot.reload()
    }
})


export default router 